/**
 * FollowUpsCard — the 360 Overview card (replaces the SOON placeholder).
 * Open follow-ups for THIS customer: title is the headline, due chip carries
 * urgency (rose overdue / amber today / slate future), ✓ completes with
 * attribution, snooze quick-picks (+1w), inline quick-add (title + date).
 * Auto-generated rows (agreement/warranty/consumable/ai) will appear here
 * with a source tag — same card, no redesign needed (FOLLOWUP-01 seam).
 */
import { useState } from "react";
import { BellRing, Check, Clock3, Plus, Sparkles, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { useAllActiveTechs } from "@/features/board/api";
import {
  useCustomerFollowups,
  useAddFollowup,
  useCompleteFollowup,
  useSnoozeFollowup,
  daysFromNowISO,
  dueLabel,
  todayISO,
  type FollowUp,
} from "./followups";
import { OfferFollowupAction, offerRef } from "./OfferFollowupAction";

const SOURCE_TAG: Record<FollowUp["source"], string | null> = {
  manual: null,
  agreement: "agreement",
  warranty: "warranty",
  consumable: "filter/part",
  ai: "suggested",
};

export function FollowUpsCard({
  custno,
  locno,
  startAdding,
}: {
  custno: string;
  locno?: string | null;
  /** STRIP+A: the Overview's quiet line mounts this card mid-add. */
  startAdding?: boolean;
}) {
  const { data, isLoading } = useCustomerFollowups(custno);
  const techs = useAllActiveTechs().data;
  const techFirst = (empno: string) => {
    const t = (techs ?? []).find((x) => x.empno === empno);
    return t ? ((t.firstname ?? "").trim() || (t.empname ?? "").trim().split(/\s+/)[0] || empno) : empno;
  };
  const add = useAddFollowup();
  const complete = useCompleteFollowup();
  const snooze = useSnoozeFollowup();
  const [adding, setAdding] = useState(!!startAdding);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState(daysFromNowISO(7));
  const [repeat, setRepeat] = useState<number | "">("");

  const open = data ?? [];

  const submit = () => {
    if (!title.trim()) return;
    add.mutate(
      {
        custno,
        locno,
        title,
        due_date: due || todayISO(),
        repeat_months: repeat === "" ? null : repeat,
      },
      {
        onSuccess: () => {
          setTitle("");
          setDue(daysFromNowISO(7));
          setRepeat("");
          setAdding(false);
        },
      },
    );
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-amber-50/70 via-transparent to-transparent px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <BellRing className="h-4 w-4" />
          </span>
          <span className="text-[13px] font-semibold uppercase tracking-wider text-slate-600">
            Follow-ups &amp; reminders
          </span>
        </div>
        {/* The button flips to Close while the form is open (Seth 2026-08-18:
            "if I click add but then don't want to add there's no way to close
            the module") — plus a Cancel in the form and Esc anywhere in it. */}
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1 text-[13px] font-medium text-brand-700 transition-colors hover:text-brand-900"
        >
          {adding ? (
            <>
              <X className="h-3.5 w-3.5" />
              Close
            </>
          ) : (
            <>
              <Plus className="h-3.5 w-3.5" />
              Add
            </>
          )}
        </button>
      </div>

      <div className="px-4 py-3">
        {adding && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setAdding(false);
            }}
            className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-slate-50/60 p-2"
          >
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Call back about the quote…"
              className="min-w-[180px] flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
            <input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              className="rounded-md border border-hairline bg-white px-2 py-1.5 text-sm text-slate-700"
            />
            {/* FOLLOWUP-02a: recurrence — completing a repeating one spawns
                the next (e.g. "Replace BreatheClean bulb" every 12 months). */}
            <select
              value={repeat}
              onChange={(e) =>
                setRepeat(e.target.value === "" ? "" : Number(e.target.value))
              }
              title="Repeats — when you complete it, the next one is created automatically"
              className="rounded-md border border-hairline bg-white px-2 py-1.5 text-sm text-slate-700"
            >
              <option value="">Doesn&apos;t repeat</option>
              <option value="1">Every month</option>
              <option value="3">Every 3 months</option>
              <option value="6">Every 6 months</option>
              <option value="12">Every year</option>
            </select>
            <button
              type="submit"
              disabled={!title.trim() || add.isPending}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {add.isPending ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="rounded-md px-2 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              Cancel
            </button>
          </form>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        ) : open.length === 0 && !adding ? (
          <p className="text-sm text-slate-500">Nothing pending.</p>
        ) : (
          <ul className="space-y-2.5">
            {open.map((f) => {
              const dl = dueLabel(f);
              // PLAN-OFFER-01 E: a plan-offer follow-up wears its own tag + one-tap Resend.
              const oref = offerRef(f.source_ref);
              const tag = oref ? "plan offer" : SOURCE_TAG[f.source];
              return (
                <li key={f.id} className="group flex items-start gap-2.5">
                  <button
                    type="button"
                    onClick={() => complete.mutate(f)}
                    title={
                      f.repeat_months
                        ? `Done — and the next one (in ${f.repeat_months} mo) is created automatically`
                        : "Done — clears it with your name on it"
                    }
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-300 text-transparent transition-colors hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-600"
                  >
                    <Check className="h-3 w-3" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium leading-snug text-slate-800">
                      {f.title}
                      {tag && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-brand-50 px-1.5 py-0.5 align-middle text-[10px] font-semibold text-brand-700">
                          {f.source === "ai" && <Sparkles className="h-2.5 w-2.5" />}
                          {tag}
                        </span>
                      )}
                    </div>
                    {f.details && (
                      <div className="text-xs text-slate-500">{f.details}</div>
                    )}
                    <div className="mt-0.5 flex items-center gap-2">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-xs font-semibold",
                          dl.tone === "overdue" && "bg-rose-50 text-rose-700",
                          dl.tone === "today" && "bg-amber-50 text-amber-800",
                          dl.tone === "future" && "bg-slate-100 text-slate-600",
                          dl.tone === "snoozed" && "bg-slate-100 text-slate-400",
                        )}
                      >
                        {dl.text}
                      </span>
                      {f.assigned_empno && (
                        <span className="rounded-full bg-sky-50 px-1.5 py-px text-[11px] font-medium text-sky-700" title="A tech set this from the field and is handling it himself — it sits on his phone until he clears it">
                          {techFirst(f.assigned_empno)} handles it
                        </span>
                      )}
                      {oref && <OfferFollowupAction sourceRef={f.source_ref} />}
                      {f.repeat_months && (
                        <span
                          className="rounded-full bg-slate-100 px-1.5 py-px text-[11px] font-medium text-slate-500"
                          title="Recurring — completing it creates the next one"
                        >
                          ↻ every{" "}
                          {f.repeat_months === 1
                            ? "month"
                            : f.repeat_months === 12
                              ? "year"
                              : `${f.repeat_months} mo`}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => snooze.mutate({ id: f.id, until: daysFromNowISO(7) })}
                        title="Snooze a week — it comes back, promises don't vanish"
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 opacity-0 transition-all hover:text-slate-600 group-hover:opacity-100"
                      >
                        <Clock3 className="h-3 w-3" />
                        Snooze 1w
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {(complete.isError || add.isError || snooze.isError) && (
          <p className="mt-2 text-xs text-red-600">
            That didn&apos;t save — try again.
          </p>
        )}
      </div>
    </Card>
  );
}
